/**
 * iter1420 · Shared tri-currency FX helper (frontend).
 *
 * Single source of truth for BOTH `SmartLinkDocumentModal` and
 * `RecordSettlementModal` so the rate direction the user sees is
 * ALWAYS the same as what the backend `fx_rate_resolver` expects.
 *
 * Pure boolean logic over the currency triple — zero enum, zero
 * hardcoded case list. Works for any new currency configured by any
 * new tenant.
 *
 * @param docCcy  Document currency (e.g. USD).
 * @param txCcy   Bank / transaction currency (e.g. GBP).
 * @param funcCcy Business functional currency (e.g. USD).
 * @returns `[from, to]` for the ONE rate the user should enter, or
 *          `null` if no FX conversion is needed.
 *
 * Rules:
 *   doc == tx == func    → null                      (no FX)
 *   doc == tx            → [tx, func]                (bank leg reval)
 *   doc == func          → [tx, doc]                 (tx → doc = func)
 *   tx  == func          → [doc, tx]                 (doc → tx = func)
 *   all three different  → [doc, tx]                 (primary rate; tx→func
 *                                                     is auto-derived from
 *                                                     cache and shown as
 *                                                     a read-only preview)
 */
export function pickRateDirection(docCcy, txCcy, funcCcy) {
  const d = String(docCcy || '').toUpperCase();
  const t = String(txCcy || '').toUpperCase();
  const f = String(funcCcy || '').toUpperCase();
  if (!d || !t || !f) return null;
  if (d === t && d === f) return null;
  if (d === t) return [t, f];
  if (d === f) return [t, d];
  if (t === f) return [d, t];
  return [d, t];
}

/**
 * True when the modal must display an FX-rate input field.
 * Mirrors backend `bank_feed_composite_settlement._needs_fx_rate`
 * plus the iter1420 tx-vs-functional extension.
 */
export function needsFxInput(docCcy, txCcy, funcCcy) {
  return pickRateDirection(docCcy, txCcy, funcCcy) !== null;
}

/**
 * Convert a doc-ccy amount into tx-ccy using the ONE user rate.
 * Returns the tx-ccy value or the identity when no conversion applies.
 */
export function docToTxCcy(amount, docCcy, txCcy, funcCcy, userRate) {
  const dir = pickRateDirection(docCcy, txCcy, funcCcy);
  if (!dir) return amount;
  const rate = parseFloat(userRate) || 0;
  if (rate <= 0) return amount;
  const [from, to] = dir;
  const d = String(docCcy || '').toUpperCase();
  const t = String(txCcy || '').toUpperCase();
  // Rate is `from → to`. We want `d → t`.
  if (from === d && to === t) return amount * rate;
  if (from === t && to === d) return amount / rate;
  // Rate direction is neither doc→tx nor tx→doc directly (three-way
  // case E: rate is doc→tx already — same as first branch).
  return amount * rate;
}

/**
 * Convert a tx-ccy amount into doc-ccy — the inverse of above.
 */
export function txToDocCcy(amount, docCcy, txCcy, funcCcy, userRate) {
  const dir = pickRateDirection(docCcy, txCcy, funcCcy);
  if (!dir) return amount;
  const rate = parseFloat(userRate) || 0;
  if (rate <= 0) return amount;
  const [from, to] = dir;
  const d = String(docCcy || '').toUpperCase();
  const t = String(txCcy || '').toUpperCase();
  if (from === d && to === t) return amount / rate;
  if (from === t && to === d) return amount * rate;
  return amount / rate;
}
